'use client'

import type { NamespacesPossibleKeys } from '@/lib/i18n/types'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { Link } from '@/ui/links/link/Link.client'
import { Button } from '../buttons/button/Button.client'

type ReadMoreLinkProps =
  | {
      type: 'expand'
      isExpanded: boolean
      setIsExpanded: (isExpanded: boolean) => void
      targetId?: never
      trackingData?: TrackingGaEvent
      expandLabelKey?: NamespacesPossibleKeys<'generic'>
      collapseLabelKey?: NamespacesPossibleKeys<'generic'>
    }
  | {
      type: 'navigate'
      targetId: `#${string}`
      isExpanded?: never
      setIsExpanded?: never
      trackingData?: TrackingGaEvent
      expandLabelKey?: NamespacesPossibleKeys<'generic'>
      collapseLabelKey?: never
    }

/**
 * Renders the 'Read More' link.
 *
 * @param targetId - The id of the target element to scroll to.
 * @returns The JSX element for the 'Read More' link.
 */
export function ReadMoreLink({
  type,
  targetId,
  isExpanded,
  setIsExpanded,
  trackingData,
  expandLabelKey = 'generic:actions.readMore',
  collapseLabelKey = 'generic:actions.readLess',
}: ReadMoreLinkProps) {
  const { t } = useTranslation(['generic'])

  if (type === 'navigate') {
    return (
      <Link
        variant="free"
        bold
        underlined="invert"
        className="text-nowrap"
        data-testid="read-more-link"
        href={targetId}
        trackingData={trackingData}
        intent={INTENT_SAME_AS_WEB}
      >
        {t(expandLabelKey)}
      </Link>
    )
  }

  return (
    <Button
      className="text-nowrap"
      variant="link"
      onPress={() => setIsExpanded(!isExpanded)}
      data-testid="read-more-link"
      trackingData={!isExpanded ? trackingData : undefined}
      intent={INTENT_SAME_AS_WEB}
    >
      {isExpanded ? t(collapseLabelKey) : t(expandLabelKey)}
    </Button>
  )
}
